import React, { useEffect, useMemo, useState, useCallback, memo } from "react";
import { useTick } from "@/lib/ticks";
import { api, fmtNum } from "@/lib/api";
import { useDebounced } from "@/lib/useDebounced";
import { useInterval } from "@/lib/useInterval";
import OrderModal from "@/components/OrderModal";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Plus, X, ChevronDown } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  // Perf (2026-05-29): Dashboard no longer subscribes to the full ticks context.
  // Individual <LiveScripCard> components subscribe per-symbol via useTick(sym)
  // so they refresh independently. The parent Dashboard now re-renders only
  // when watchlist/banned/search/pending state changes — NOT on every WS batch
  // (was 1×/sec parent re-render). This dramatically reduces the JS thread work
  // during heavy tick traffic, eliminating modal-open stutter.
  const [q, setQ] = useState("");
  const qDebounced = useDebounced(q, 150);
  const [banned, setBanned] = useState([]);
  const [watchSyms, setWatchSyms] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [addExchange, setAddExchange] = useState("MCX");
  const [addQuery, setAddQuery] = useState("");
  const addQueryDebounced = useDebounced(addQuery, 150);
  const [availSymbols, setAvailSymbols] = useState([]);
  const [availLoading, setAvailLoading] = useState(false);
  const [orderSym, setOrderSym] = useState(null);
  const [orderSide, setOrderSide] = useState("BUY");
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState([]);

  const loadWatchlist = useCallback(() =>
    api.get("/watchlist").then((r) => setWatchSyms(r.data.symbols || [])).catch(() => {}), []);
  const loadPending = useCallback(() =>
    api.get("/orders/pending").then((r) => setPending(r.data.items || [])).catch(() => {}), []);

  const loadAvailable = useCallback((exch) => {
    setAvailLoading(true);
    api.get(`/symbols/available?exchange=${exch}`)
      .then((r) => setAvailSymbols(r.data.items || []))
      .catch(() => setAvailSymbols([]))
      .finally(() => setAvailLoading(false));
  }, []);

  useEffect(() => {
    api.get("/symbols").then((r) => setBanned(r.data.banned || [])).catch(() => {});
    loadWatchlist();
  }, [loadWatchlist]);

  // Pending orders poll — visibility-aware (paused on bg tab; WS broadcasts new orders too)
  useInterval(loadPending, 15000);

  useEffect(() => {
    if (showAdd) loadAvailable(addExchange);
  }, [showAdd, addExchange, loadAvailable]);

  // Watchlist set (O(1) lookup) — stable across renders unless watchSyms changes
  const watchSet = useMemo(() => new Set(watchSyms), [watchSyms]);
  const bannedSet = useMemo(() => new Set(banned), [banned]);

  // Filtered watchlist (Default view only — NSEFO/MCX tabs removed per Session 27).
  // We derive the symbol list from `watchSyms` (insertion order from backend
  // $addToSet) and search query alone. The price/LTP/UC/LC for each symbol is
  // pulled by each <LiveScripCard> via useTick(sym) — so this list memo no
  // longer depends on the ticks proxy and parent Dashboard does NOT re-render
  // on every WS batch.
  const list = useMemo(() => {
    const ql = qDebounced ? qDebounced.toLowerCase() : "";
    if (!ql) return watchSyms;
    return watchSyms.filter((s) => s.toLowerCase().includes(ql));
  }, [watchSyms, qDebounced]);

  // Stable handlers — prevent ScripCard React.memo cache misses
  const handleBuy = useCallback((sym) => { setOrderSym(sym); setOrderSide("BUY"); setOpen(true); }, []);
  const handleSell = useCallback((sym) => { setOrderSym(sym); setOrderSide("SELL"); setOpen(true); }, []);
  const handleTap = useCallback((sym) => { setOrderSym(sym); setOrderSide("BUY"); setOpen(true); }, []);

  const addToWatch = useCallback(async (sym) => {
    try { await api.post("/watchlist", { symbol: sym }); toast.success(`${sym} added`); loadWatchlist(); }
    catch { toast.error("Failed"); }
  }, [loadWatchlist]);

  const removeWatch = useCallback(async (sym) => {
    try { await api.delete(`/watchlist/${sym}`); loadWatchlist(); } catch {}
  }, [loadWatchlist]);

  const cancelPending = useCallback(async (id) => {
    try { await api.delete(`/orders/${id}`); toast.success("Cancelled"); loadPending(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }, [loadPending]);

  const filteredAvail = useMemo(() => {
    if (!addQueryDebounced) return availSymbols;
    const ql = addQueryDebounced.toLowerCase();
    return availSymbols.filter((s) => s.symbol.toLowerCase().includes(ql));
  }, [availSymbols, addQueryDebounced]);

  const pendingOpen = useMemo(() => pending.filter((o) => o.status === "PENDING"), [pending]);

  return (
    <div className="space-y-3" data-testid="dashboard-page">
      <div className="flex items-center gap-2 min-w-0">
        <Pill label="Default"
              testid="exchange-pill"
              onClick={() => {}} />

        <button onClick={() => { setAddExchange((p) => p === "MCX" ? "NSEFO" : "MCX"); }}
                data-testid="add-exchange-toggle"
                className="shrink-0 flex items-center gap-1.5 h-9 px-3 rounded-lg bg-card border border-border text-sm font-bold">
          '{addExchange}'
          <ChevronDown className="h-4 w-4 opacity-60" />
        </button>

        <button onClick={() => setShowAdd(true)} data-testid="add-watchlist-btn"
                className="flex-1 min-w-0 flex items-center justify-between gap-1.5 h-9 px-3 rounded-lg bg-card border border-border text-sm font-medium text-muted-foreground hover:bg-secondary transition-colors">
          <span className="truncate">Select Scrip To Add</span>
          <ChevronDown className="h-4 w-4 shrink-0" />
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search symbol…" className="pl-9 h-9 bg-card" data-testid="search-input" />
      </div>

      <div className="space-y-2">
        {list.length === 0 && (
          <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground"
               data-testid="empty-scrips">
            Your watchlist is empty. Tap 'Select Scrip To Add' to start.
          </div>
        )}
        {list.map((sym) => (
          <LiveScripCard key={sym} symbol={sym}
                         isBanned={bannedSet.has(sym)}
                         inWatch={watchSet.has(sym)}
                         showRemove={true}
                         onTap={handleTap}
                         onBuy={handleBuy}
                         onSell={handleSell}
                         onRemove={removeWatch} />
        ))}
      </div>

      {pendingOpen.length > 0 && (
        <>
          <div className="text-center text-xs font-bold uppercase tracking-wider text-muted-foreground mt-4 mb-1.5">
            Pending Orders
          </div>
          <div className="space-y-2">
            {pendingOpen.map((o) => (
              <PendingPill key={o.id} o={o} onCancel={() => cancelPending(o.id)} />
            ))}
          </div>
        </>
      )}

      {/* OrderModal: conditionally rendered — only mounts when user clicks BUY/SELL.
          Previously was always mounted with `open={false}`, subscribing to ticks
          for the last-selected symbol even after close. Conditional mount also
          fixes the modal-open stutter (the heavy Radix portal + focus-trap +
          scroll-lock work only happens at click time, not at Dashboard mount). */}
      {open && (
        <OrderModal open={open} onOpenChange={setOpen} symbol={orderSym} side={orderSide}
                    onPlaced={loadPending} />
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col" data-testid="add-watchlist-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2">
              <span>Add to Watchlist</span>
              <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
                <button onClick={() => setAddExchange("MCX")}
                        data-testid="modal-tab-mcx"
                        className={`px-3 py-1 ${addExchange === "MCX" ? "bg-primary text-primary-foreground" : "bg-card"}`}>MCX</button>
                <button onClick={() => setAddExchange("NSEFO")}
                        data-testid="modal-tab-nsefo"
                        className={`px-3 py-1 ${addExchange === "NSEFO" ? "bg-primary text-primary-foreground" : "bg-card"}`}>NSEFO</button>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="relative shrink-0">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={addQuery} onChange={(e) => setAddQuery(e.target.value)}
                   placeholder={`Search ${addExchange} scrips…`}
                   className="pl-9 h-9" data-testid="modal-search-input" />
          </div>

          <div className="overflow-auto flex-1 space-y-0.5 mt-2">
            {availLoading && <div className="text-center text-sm text-muted-foreground py-8">Loading…</div>}
            {!availLoading && filteredAvail.length === 0 && (
              <div className="text-center text-sm text-muted-foreground py-8">
                {addQueryDebounced ? "No match" : "No scrips available"}
              </div>
            )}
            {!availLoading && filteredAvail.map((s) => (
              <AvailRow key={s.symbol} s={s} inWatch={watchSet.has(s.symbol)}
                        onAdd={addToWatch} onRemove={removeWatch} />
            ))}
          </div>
          <div className="text-[11px] text-muted-foreground text-center pt-2 border-t border-border">
            {filteredAvail.length} {addExchange} scrips · {watchSyms.length} in watchlist
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const AvailRow = memo(function AvailRow({ s, inWatch, onAdd, onRemove }) {
  return (
    <button data-testid={`avail-${s.symbol}`}
            onClick={() => inWatch ? onRemove(s.symbol) : onAdd(s.symbol)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md hover:bg-secondary text-sm">
      <div className="flex flex-col items-start min-w-0">
        <span className="font-mono text-xs font-semibold truncate">{s.symbol}</span>
        <span className="text-[10px] text-muted-foreground">Lot {s.lot_size} · {s.expiry || "—"}</span>
      </div>
      {inWatch
        ? <X className="h-4 w-4 text-destructive shrink-0" />
        : <Plus className="h-4 w-4 text-primary shrink-0" />}
    </button>
  );
});

function Pill({ label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className="shrink-0 flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-bold text-white"
            style={{ background: "hsl(var(--header))" }}>
      {label}
      <ChevronDown className="h-4 w-4 opacity-80" />
    </button>
  );
}

/**
 * LiveScripCard — subscribes to its OWN symbol via useTick(). Cards for symbols
 * that didn't tick this batch DO NOT re-render. Massive perf win on big lists.
 * Outer props (isBanned/inWatch/handlers) are stable refs from parent useCallback.
 */
const LiveScripCard = memo(function LiveScripCard({ symbol, isBanned, inWatch, showRemove, onTap, onBuy, onSell, onRemove }) {
  const t = useTick(symbol);
  if (!t) return null;
  const isExpired = !!t.expired_at || t.is_active === false;
  const chg = t.prev_close ? ((t.ltp - t.prev_close) / t.prev_close) * 100 : 0;
  const up = chg >= 0;
  const sideColor = up ? "hsl(var(--buy))" : "hsl(var(--sell))";
  return (
    <div className={`rounded-xl bg-card border shadow-sm overflow-hidden flex ${isExpired ? "opacity-60 border-destructive" : "border-border"}`}
         data-testid={`scrip-card-${symbol}`}>
      <div className="w-1" style={{ background: isExpired ? "hsl(var(--destructive))" : sideColor }} />
      <div className="flex-1 px-3 py-2.5 flex items-center justify-between gap-2">
        <button onClick={isExpired ? null : () => onTap(symbol)} disabled={isExpired}
                className="text-left flex-1 min-w-0 disabled:cursor-not-allowed">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-mono text-[13px] font-bold truncate" data-testid={`scrip-name-${symbol}`}>
              {symbol}
            </span>
            {isExpired && <span data-testid={`expired-badge-${symbol}`}
                                 className="text-[8px] uppercase tracking-wider px-1 py-0 rounded bg-destructive text-destructive-foreground font-bold">EXPIRED</span>}
            {isBanned && !isExpired && <span className="text-[8px] uppercase tracking-wider px-1 py-0 rounded bg-destructive text-destructive-foreground">BAN</span>}
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5 num">
            {fmtNum(t.prev_close)} <span className={up ? "text-profit" : "text-loss"}>| {chg.toFixed(2)}</span>
          </div>
        </button>
        <div className="flex items-stretch gap-1 shrink-0">
          {!isExpired && <>
            <button onClick={() => onSell(symbol)} data-testid={`sell-${symbol}`}
                    className="rounded-md px-2 py-1 text-white text-right min-w-[58px]"
                    style={{ background: "hsl(var(--sell))" }}>
              <div className="text-[12px] font-extrabold num leading-tight">{fmtNum(t.bid)}</div>
              <div className="text-[9px] opacity-80 num leading-tight">L: {fmtNum(t.low)}</div>
            </button>
            <button onClick={() => onBuy(symbol)} data-testid={`buy-${symbol}`}
                    className="rounded-md px-2 py-1 text-white text-right min-w-[58px]"
                    style={{ background: "hsl(var(--buy))" }}>
              <div className="text-[12px] font-extrabold num leading-tight">{fmtNum(t.ask)}</div>
              <div className="text-[9px] opacity-80 num leading-tight">H: {fmtNum(t.high)}</div>
            </button>
          </>}
          {showRemove && (
            <button onClick={() => onRemove(symbol)} data-testid={`remove-watch-${symbol}`}
                    className="h-full w-6 grid place-items-center rounded-md bg-muted hover:bg-destructive hover:text-destructive-foreground transition-colors">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

function PendingPill({ o, onCancel }) {
  const isBuy = o.side === "BUY";
  const bg = isBuy ? "hsl(var(--buy))" : "hsl(var(--sell))";
  return (
    <div className="rounded-xl text-white shadow-sm flex items-center justify-between px-3 py-2.5"
         style={{ background: bg }}
         data-testid={`pending-pill-${o.order_no}`}>
      <div className="min-w-0">
        <div className="font-mono text-[12px] font-bold truncate">{o.symbol}</div>
        <div className="text-[10px] opacity-80">
          {o.order_type} · {new Date(o.placed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="text-right">
          <div className="text-[12px] font-bold num">{o.qty} @ {o.price ? fmtNum(o.price) : "MKT"}</div>
          <div className="text-[10px] opacity-80">{isBuy ? "BUY" : "SELL"}</div>
        </div>
        <button onClick={onCancel} data-testid={`cancel-pending-${o.order_no}`}
                className="h-6 w-6 grid place-items-center rounded-full bg-white/25 hover:bg-white/40">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
